import { apiClient } from '@/api/api'
import type {
  SimilarResponse,
  WardrobeBatchUnlinkResponse,
  WardrobeDeleteResponse,
  WardrobeDeletionImpact,
  WardrobeFromCatalogResponse,
  WardrobeItemDetail,
  WardrobeListResponse,
  WardrobePatchBody,
  WardrobePatchResponse,
  WardrobeUploadResponse,
} from './types'

/** Backend ceiling for `page_size` (§1.1). */
export const PLAYGROUND_PAGE_SIZE_MAX = 100

export interface WardrobeListParams {
  pageNum?: number
  pageSize?: number
  /** ML `type.id` filter, 0..5. */
  type?: number | null
  /** §1 (v2) — repeated keys `color_group_ids=1&color_group_ids=2`. */
  colorGroupIds?: number[]
  seasonIds?: number[]
  /** Decision 22 (BE-192) — one style of her Style DNA. */
  styleId?: number | null
}

/** FastAPI reads repeated keys, not `key[]`. */
const REPEATED_KEYS = { indexes: null } as const

export const getWardrobe = async (params: WardrobeListParams = {}) => {
  return apiClient.get<WardrobeListResponse>('/playground/user-wardrobe', {
    params: {
      page_num: params.pageNum ?? 1,
      page_size: params.pageSize ?? PLAYGROUND_PAGE_SIZE_MAX,
      ...(params.type != null ? { type: params.type } : {}),
      ...(params.colorGroupIds?.length ? { color_group_ids: params.colorGroupIds } : {}),
      ...(params.seasonIds?.length ? { season_ids: params.seasonIds } : {}),
      ...(params.styleId != null ? { style_id: params.styleId } : {}),
    },
    paramsSerializer: REPEATED_KEYS,
  })
}

export const getWardrobeItem = async (overviewId: number) => {
  return apiClient.get<WardrobeItemDetail>(`/playground/user-wardrobe/${overviewId}`)
}

/** Detection + crop + S3 in the request path; 1..10 keys, synchronous (§1.3). */
export const uploadWardrobe = async (imgUrls: string[]) => {
  return apiClient.post<WardrobeUploadResponse>('/playground/user-wardrobe', {
    imgUrls,
  })
}

export const patchWardrobeItem = async (overviewId: number, body: WardrobePatchBody) => {
  return apiClient.patch<WardrobePatchResponse>(`/playground/user-wardrobe/${overviewId}`, body)
}

export const getWardrobeDeletionImpact = async (overviewId: number) => {
  return apiClient.get<WardrobeDeletionImpact>(
    `/playground/user-wardrobe/${overviewId}/deletion-impact`,
  )
}

export const deleteWardrobeItem = async (overviewId: number) => {
  return apiClient.delete<WardrobeDeleteResponse>(`/playground/user-wardrobe/${overviewId}`)
}

export const batchUnlinkWardrobe = async (overviewIds: number[]) => {
  return apiClient.post<WardrobeBatchUnlinkResponse>('/playground/user-wardrobe/batch-unlink', {
    overviewIdsToUnlink: overviewIds,
  })
}

/** "I already own this" — a catalogue product joins the wardrobe without a photo (B8, §1.8). */
export const addWardrobeFromCatalog = async (productId: number, variantId?: number | null) => {
  return apiClient.post<WardrobeFromCatalogResponse>('/playground/user-wardrobe/from-catalog', {
    productId,
    ...(variantId != null ? { variantId } : {}),
  })
}

/** Catalogue neighbours of one wardrobe photo; `s3Key` must sit under the caller's prefix (§1.7). */
export const findSimilarInCatalogue = async (s3Key: string, n = 20) => {
  return apiClient.post<SimilarResponse>('/playground/similar', { s3Key, n })
}
